<script setup lang="ts">
import { Chart } from 'chart.js/auto';
import { onMounted, onBeforeUnmount, ref, watch, computed } from 'vue';
import { ComparisonResult } from '../lib/types';
import { formatCurrency } from '../lib/math';

const props = defineProps<{
  result: ComparisonResult;
}>();

const canvasRef = ref<HTMLCanvasElement | null>(null);
let chart: Chart | null = null;

const paths = computed(() =>
  [
    ...props.result.jdg.cashflowPaths.map(p => ({ ...p, source: 'JDG' })),
    ...props.result.spzoo.cashflowPaths.map(p => ({ ...p, source: 'Sp. z o.o.' }))
  ].filter(p => p.gross > 0)
);

function buildChart() {
  if (!canvasRef.value) return;
  if (chart) chart.destroy();

  const labels = paths.value.map(p => p.label);
  chart = new Chart(canvasRef.value, {
    type: 'bar',
    data: {
      labels,
      datasets: [
        {
          label: 'Net to owner',
          data: paths.value.map(p => p.net),
          backgroundColor: '#22c55e',
          stack: 'tax-paths'
        },
        {
          label: 'Taxes paid',
          data: paths.value.map(p => p.taxes),
          backgroundColor: '#f97316',
          stack: 'tax-paths'
        }
      ]
    },
    options: {
      responsive: true,
      indexAxis: 'y',
      scales: {
        x: { stacked: true, beginAtZero: true },
        y: { stacked: true }
      },
      plugins: {
        tooltip: {
          callbacks: {
            label(context) {
              const path = paths.value[context.dataIndex];
              const value = context.raw as number;
              const share = path.gross > 0 ? ((value / path.gross) * 100).toFixed(1) : '0';
              return `${context.dataset.label}: ${formatCurrency(value)} (${share}%)`;
            },
            footer(context) {
              const path = paths.value[context[0].dataIndex];
              const effective = path.gross > 0 ? ((path.taxes / path.gross) * 100).toFixed(1) : '0.0';
              return `Effective tax on this path: ${effective}%`;
            }
          }
        },
        legend: {
          position: 'bottom'
        }
      }
    }
  });
}

onMounted(buildChart);
watch(() => props.result, buildChart, { deep: true });
onBeforeUnmount(() => {
  chart?.destroy();
});
</script>

<template>
  <div class="card">
    <div class="section-title">
      <span>Taxation paths</span>
      <span class="badge">Interactive · stacked</span>
    </div>
    <p class="muted">
      Compares how much tax is paid on each payout path (JDG vs. consolidated sp. z o.o. owner cash). Hover to see
      effective tax rates and amounts; green shows net to owner, orange shows taxes paid.
    </p>
    <canvas ref="canvasRef"></canvas>
  </div>
</template>
